import React, { useEffect } from 'react'
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { connect } from 'react-redux'
import { withFormik, Form, Field } from 'formik'
import * as Yup from 'yup'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useIsMounted } from '../../../hooks'
import { formatError } from '../../../utils'
import { makeGetProductById } from '../../../selectors'
import InputAdornment from '@mui/material/InputAdornment'
import ProductFormVariants from './ProductFormVariants'
import { 
  create, 
  update, 
  showAlert,
  addProductsToArtist,
  formSubmittedSuccessfully,
  formSubmittedWithErrors,
  resetFormState
} from '../../../actions'
import { 
  FormButton,
  Errors,
  FormikTextField,
  ThreeDotProgress,
  FormTitle,
  CloseButton,
  MoneyField
} from '../../generics'




const cssStyles = props => ({
  root: css`
    margin: 0;
    width: 100%;
    overflow: hidden;
    position: relative;
    display: flex;
    justify-content: center;
    align-content: flex-start;
    padding: props.isSmallScreen ? '0px 40px 0px 40px' : 0;
  ` ,
  form: props.isSmallScreen 
    ? css`
        width: 400px;
        overflow-x: hidden;
        display: flex;
        flex-direction: column;
        gap: 30px;
        padding: 40px;
      ` 
    : css`
        width: 100%;
        display: flex;
        justify-content: center;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: 80px;
      `
  ,
  outerImagesSection: css`
    flex: 1 1 50%;
    padding: 80px 20px;
    display: flex;
    justify-content: flex-end;
    overflow-y: scroll;
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  innerImagesSection: css`
    max-width: 490px;
    flex-grow: 1;
  `,
  outerFieldsSection: css`
    flex: 1 1 50%;
    padding: 80px 20px;
    overflow-y: scroll;
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  innerFieldSection: css`
    max-width: 400px;
    display: flex;
    flex-direction: column;
    gap: 25px;
  `
})




const ProductForm = props => {

  const isMounted = useIsMounted()

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'))

  const styles = cssStyles({ isSmallScreen })
  

  const {
    product,
    setFieldValue,
    errors,
    values,
    isSubmitting,
    setOpen,
    formStatus,
    formErrors,
    resetFormState,
    setSubmitting,
    setErrors,
    handleBlur
  } = props



  const unlistedErrors = (errors && errors.unlisted) ? errors.unlisted : []


  useEffect(() => {
    if (isMounted.current) {
      if (formStatus === 'success') setOpen(false)

      if (formStatus === 'error') {
        setSubmitting(false)
        setErrors(formErrors)
      }
    }

    return () => resetFormState()
  }, [
    isMounted, 
    formStatus, 
    formErrors,
    setErrors, 
    setOpen, 
    setSubmitting,
    resetFormState
  ])




  function trimValue(e, value, field) {
    handleBlur(e)

    if (typeof value === 'string' || value instanceof String) {
      const trimmed = value.trim()

      if (trimmed !== value && values[field]) {
        setFieldValue(field, trimmed)
      }
    }
  }




  return (
    <div css={ styles.root }>

      <CloseButton onClick={ () => setOpen(false) } />

      <Form css={ styles.form } autoComplete='off'>
        <div css={ styles.outerImagesSection }>
          <div css={ styles.innerImagesSection }>
          </div>
        </div>


        <div css={ styles.outerFieldsSection }>
          <div css={ styles.innerFieldSection }>
            <FormTitle 
              css={ styles.title } 
              boldText='Product' 
              text='Form'
            />


            { Boolean(unlistedErrors.length) && <Errors errors={ unlistedErrors } /> }


            <Field
              variant='outlined'
              component={ FormikTextField }
              name='name' 
              label='Product Name'
              autoComplete='off'
              fullWidth
              onBlur={ e => trimValue(e, values.name, 'name') }
            />


            <Field
              variant='outlined'
              component={ FormikTextField }
              name='caption' 
              label='Caption'
              helperText={ (50 - values.caption.length).toString() + ' characters left' }
              autoComplete='off'
              fullWidth
              onBlur={ e => trimValue(e, values.name, 'caption') }
            />


            <Field
              variant='outlined'
              component={ FormikTextField }
              name='description'
              label='Product Description'
              helperText={ (3500 - values.description.length).toString() + ' characters left' }
              fullWidth
              multiline={ true }
              rows={15}
              onBlur={ e => trimValue(e, values.description, 'description') }
            />


            <Field
              variant='outlined'
              component={ MoneyField }
              name='price'
              label='Price'
              fullWidth
              startAdornment={
                <InputAdornment position='start'>$</InputAdornment>
              }
            />


            <ProductFormVariants 
              variants={ values.variants } 
              setFieldValue={ setFieldValue } 
            />


            <FormButton 
              type='submit' 
              variant='contained' 
              color='primary'
              disabled={ isSubmitting }
            >
            { isSubmitting 
                ? <ThreeDotProgress color='#fff' radius={ 5 } />  
                : product ? 'Update' : 'Submit'
            }
            </FormButton>
          </div>
        </div>
      </Form>
    </div>
  )
}


const EnhancedProductForm = withFormik({
  mapPropsToValues: ({ product }) => {
    const { 
      name, 
      caption,
      description, 
      price,
      variants
    } = product ? product.attributes : {}


    return {
      name: name || '',
      caption: caption || '',
      description: description || '',
      price: price || '',
      variants: variants || {}
    }
  },


  validationSchema: Yup.object().shape({
    name: Yup.string().required('required').max(50),
    price: Yup.string().required('required'),
    caption: Yup.string().max(50),
    description: Yup.string().max(3500)
  }),


  handleSubmit: (values, { props }) => {
    const { 
      product, 
      create, 
      update, 
      showAlert,
      formSubmittedSuccessfully,
      formSubmittedWithErrors,
      merchantType,
      merchantId,
      addProductsToArtist
    } = props

    
    if (product) {
      // If the product exists then update the record with the new values
      const form = new FormData()

      // Main image
      if (values.image0) form.append('image', values.image0)

      // Other images
      if (values.image1) form.append('images[]', values.image1)
      if (values.image2) form.append('images[]', values.image2)
      if (values.image3) form.append('images[]', values.image3)
      if (values.image4) form.append('images[]', values.image4)
      if (values.image5) form.append('images[]', values.image5)
      if (values.image6) form.append('images[]', values.image6)
      if (values.image7) form.append('images[]', values.image7)
      if (values.image8) form.append('images[]', values.image8)


      form.append('name', values.name)
      form.append('price', values.price)
      form.append('caption', values.caption)
      form.append('description', values.description)
      form.append('variants', JSON.stringify(values.variants))



      update('/products/' + product.id, 'PRODUCT', form).then(response => {
        formSubmittedSuccessfully()
        showAlert('Information updated', 'success')
      })
      .catch(error => {
        error = formatError(error)
        formSubmittedWithErrors(error.messages, values)
      })

    } else { 
      // If the product does not exist create a new record with the form values
      const url = '/' + merchantType + 's/' + merchantId + '/products'

      const form = new FormData()

      // Main image
      if (values.image0) form.append('image', values.image0)

      // Other images
      if (values.image1) form.append('images[]', values.image1)
      if (values.image2) form.append('images[]', values.image2)
      if (values.image3) form.append('images[]', values.image3)
      if (values.image4) form.append('images[]', values.image4)
      if (values.image5) form.append('images[]', values.image5)
      if (values.image6) form.append('images[]', values.image6)
      if (values.image7) form.append('images[]', values.image7)
      if (values.image8) form.append('images[]', values.image8)


      form.append('name', values.name)
      form.append('price', values.price)
      form.append('caption', values.caption)
      form.append('description', values.description)
      form.append('variants', JSON.stringify(values.variants))


      create(url, 'PRODUCT', form).then(response => {
        formSubmittedSuccessfully()

        switch (merchantType) {
          case 'artist':
            addProductsToArtist(merchantId, response.data)
            break
          case 'venue':
            break
          case 'band':
            break
          default:
            break
        }

        showAlert('Product created', 'success')
      })
      .catch(error => {
        error = formatError(error)
        formSubmittedWithErrors(error.messages, values)
      })
    }
  }
})(ProductForm)


const makeMapStateToProps = () => {
  const getProductById = makeGetProductById()

  const mapStateToProps = (state, props) => {
    const id = props.productId

    return {
      product: id ? getProductById(state, id) : null,
      formStatus: state.form.status,
      formErrors: state.form.errors
    }
  }

  return mapStateToProps
}


const actions = {
  create,
  update, 
  showAlert,
  addProductsToArtist,
  formSubmittedSuccessfully,
  formSubmittedWithErrors,
  resetFormState
}

export default connect(makeMapStateToProps, actions)(EnhancedProductForm)




